import { createSelector } from '@reduxjs/toolkit';
import Button from '@rio-cloud/rio-uikit/Button';
import { getTrackingAttributes } from '@rio-cloud/rio-uikit/googleAnalyticsUtils';
import { CSVLink } from 'react-csv';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAppSelector } from '../../../../configuration/setup/hooks';
import { getAssociatedDevices } from '../../reducers/assets/assetsSlice';
import { getTags } from '../../reducers/tags/tagsSlice';
import { type AssetExportColumnKey, getDataForCsvLink, getVisibleExportColumnKeys } from './CsvExport.helper';
import type { Asset, TableColumnDetails } from './types';

const getDevices = createSelector([getAssociatedDevices], devices => {
    if (!devices) {
        return {};
    }
    return Object.fromEntries(
        Object.entries(devices).map(([assetId, associatedDevicesList]) => {
            return [assetId, associatedDevicesList.map(associatedDevice => associatedDevice.device)];
        })
    );
});

export const CsvExport = (props: { assets: Asset[]; columns: TableColumnDetails[] }) => {
    const availableTags = useAppSelector(getTags);
    const devices = useAppSelector(getDevices);

    const intl = useIntl();
    const exportColumnKeys = getVisibleExportColumnKeys(props.columns);
    const columnLabelsByKey = {
        name: intl.formatMessage({ id: 'intl-msg:asset.name' }),
        identification: intl.formatMessage({ id: 'intl-msg:asset.identification_type.vin' }),
        license_plate: intl.formatMessage({ id: 'intl-msg:asset.license-plate' }),
        brand: intl.formatMessage({ id: 'intl-msg:asset.brand' }),
        fuel_type: intl.formatMessage({ id: 'intl-msg:masterdata.fuel-type' }),
        type: intl.formatMessage({ id: 'intl-msg:asset.type' }),
        status: intl.formatMessage({ id: 'intl-msg:asset.status' }),
        tags: intl.formatMessage({ id: 'intl-msg:asset.tags' }),
        devices: intl.formatMessage({ id: 'intl-msg:asset.devices' }),
    } satisfies Record<AssetExportColumnKey, string>;

    const headers = exportColumnKeys.map(columnKey => ({
        label: columnLabelsByKey[columnKey],
        key: columnKey,
    }));

    const data = getDataForCsvLink(props.assets, availableTags, devices);

    return (
        <CSVLink
            filename={'RIO-Assets.csv'}
            data={data}
            headers={headers}
            {...getTrackingAttributes({
                trigger: 'click',
                category: 'link autoTracking',
                action: 'link click',
                label: 'intl-msg:csv-export',
            })}
        >
            <Button bsStyle={Button.DEFAULT} iconName='rioglyph-download'>
                <FormattedMessage id={'intl-msg:csv-export'} />
            </Button>
        </CSVLink>
    );
};
